import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const premiumBadgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Primary - Ultramarine Purple
        primary: "border-primary/20 bg-primary/10 text-primary hover:bg-primary/20 shadow-sm",
        
        // Accent - Electric Cherry
        accent: "border-accent/20 bg-accent/10 text-accent hover:bg-accent/20 shadow-sm",
        
        // Secondary
        secondary: "border-secondary/20 bg-secondary/10 text-secondary-foreground hover:bg-secondary/20",
        
        // Success - Green
        success: "border-success/20 bg-success/10 text-success hover:bg-success/20 shadow-sm",
        
        // Warning - Yellow
        warning: "border-warning/20 bg-warning/10 text-warning hover:bg-warning/20 shadow-sm",
        
        // Destructive - Electric Cherry
        destructive: "border-destructive/20 bg-destructive/10 text-destructive hover:bg-destructive/20 shadow-sm",
        
        // Info - Blue
        info: "border-info/20 bg-info/10 text-info hover:bg-info/20 shadow-sm",
        
        // Neutral
        neutral: "border-neutral-200 bg-neutral-50 text-neutral-700 hover:bg-neutral-100",
        
        // Outline
        outline: "border-border bg-transparent text-foreground hover:bg-accent/10 hover:text-accent",
        
        // Ghost
        ghost: "border-transparent bg-transparent text-muted-foreground hover:bg-accent/10 hover:text-accent",
        
        // Conversation Status Colors
        active: "border-green-200 bg-green-50 text-green-700 hover:bg-green-100 shadow-sm",
        priority: "border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 shadow-sm",
        escalated: "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 shadow-sm",
        "ai-processing": "border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 shadow-sm",
        
        // Premium Variants
        premium: "border-neutral-300 bg-gradient-to-r from-neutral-50 to-neutral-100 text-neutral-800 hover:from-neutral-100 hover:to-neutral-200 shadow-md",
        glass: "glass border-white/30 text-foreground hover:bg-white/90",
        "glass-dark": "glass-dark border-white/20 text-white hover:bg-black/50",
      },
      size: {
        sm: "px-2 py-0.5 text-xs",
        default: "px-2.5 py-0.5 text-xs",
        lg: "px-3 py-1 text-sm",
        xl: "px-4 py-1.5 text-sm font-semibold",
      },
      glow: {
        none: "",
        subtle: "shadow-sm",
        medium: "shadow-md shadow-current/20",
        strong: "shadow-lg shadow-current/30",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      glow: "none",
    },
  }
)

export interface PremiumBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof premiumBadgeVariants> {
  icon?: React.ReactNode
  dot?: boolean
}

const PremiumBadge = React.forwardRef<HTMLDivElement, PremiumBadgeProps>(
  ({ className, variant, size, glow, icon, dot, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(premiumBadgeVariants({ variant, size, glow, className }))}
      {...props}
    >
      {dot && (
        <div className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      )}
      {icon && <span className="text-current">{icon}</span>}
      {children}
    </div>
  )
)
PremiumBadge.displayName = "PremiumBadge"

export { PremiumBadge, premiumBadgeVariants } 